/**
 * StatsPage
 *
 * Dedicated route for player career statistics.
 * Supports two URL patterns:
 *   /stats                → shows first registered sport (or sport selector)
 *   /stats/:sportSlug     → shows stats for that sport (by name slug, e.g. "badminton")
 *
 * The sport slug is the sport name lowercased with spaces replaced by underscores.
 * Examples: "badminton" → slug "badminton", "table tennis" → "table_tennis"
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserSports } from '../services/api/sportsService';
import PlayerSportStats from '../components/user/sports/PlayerSportStats';
import GameLayout from '../components/layout/GameLayout';

import { toSlug } from '../utils/sportSlug';

function StatsPage({ token, onLogout }) {
  const { sportSlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [userSports, setUserSports] = useState([]);
  const [activeSport, setActiveSport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Keep a ref so the slug-resolution effect can read sports without re-fetching
  const userSportsRef = useRef([]);

  // Effect 1: Fetch sports ONCE per user session — does NOT depend on sportSlug
  // so navigating between /stats/badminton and /stats/volleyball never re-fetches.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUserSports(user.id)
      .then((data) => {
        if (cancelled) return;
        const sports = Array.isArray(data?.data) ? data.data
          : Array.isArray(data) ? data : [];
        userSportsRef.current = sports;
        setUserSports(sports);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load sports');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Effect 2: Resolve the active sport from the URL slug whenever the slug
  // changes OR when sports first load. No API call — pure local resolution.
  useEffect(() => {
    if (!userSports.length) return;
    const target = sportSlug
      ? (userSports.find((s) => toSlug(s.name) === sportSlug) || userSports[0])
      : userSports[0];
    setActiveSport((prev) => (prev?.id === target?.id ? prev : target));
  }, [sportSlug, userSports]);

  const handleSportChange = (sport) => {
    setActiveSport(sport);
    navigate(`/stats/${toSlug(sport.name)}`, { replace: true });
  };

  if (loading || (userSports.length > 0 && !activeSport)) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#e2e8f0' }}>
          <div>⏳ Loading stats…</div>
        </div>
      </GameLayout>
    );
  }

  if (error) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#f87171' }}>
          <p>⚠️ {error}</p>
          <button onClick={() => navigate('/dashboard')} style={{ marginTop: '1rem', padding: '0.5rem 1.25rem', background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.5)', borderRadius: '0.5rem', color: '#e2e8f0', cursor: 'pointer' }}>
            ← Back to Dashboard
          </button>
        </div>
      </GameLayout>
    );
  }

  if (userSports.length === 0) {
    return (
      <GameLayout token={token} onLogout={onLogout} profile={user}>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <h3 style={{ color: '#e2e8f0', marginBottom: '0.5rem' }}>No Sports Yet</h3>
          <p>Add sports to your profile to start tracking career statistics.</p>
          <button onClick={() => navigate('/sports')} style={{ marginTop: '1rem', padding: '0.5rem 1.25rem', background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.5)', borderRadius: '0.5rem', color: '#e2e8f0', cursor: 'pointer' }}>
            Add Sports
          </button>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout token={token} onLogout={onLogout} profile={user}>
      <PlayerSportStats
        sport={activeSport}
        sports={userSports}
        onSportChange={handleSportChange}
        onBack={() => navigate('/dashboard')}
      />
    </GameLayout>
  );
}

export default StatsPage;
